/**
 * Frontend Chatbot Widget.
 *
 * Uses direct React imports and native fetch — no @wordpress/* dependencies
 * beyond the JSX runtime that wp-scripts handles automatically.
 */
import { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { marked } from 'marked';
import './style.css';

/**
 * Get or create a persistent visitor cookie ID for personalization.
 */
function getVisitorId() {
    const cookieName = 'ai_personalization_id';
    const match = document.cookie.match(new RegExp('(^| )' + cookieName + '=([^;]+)'));
    if (match) return match[2];

    const id = 'visitor_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    document.cookie = `${cookieName}=${id};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`;
    return id;
}

const Chatbot = () => {
    const visitorId = useRef(getVisitorId()).current;
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: 'assistant', content: 'Hi there! How can I personalize your experience today?' },
    ]);
    const [draft, setDraft] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    // Load chat history on first open.
    useEffect(() => {
        if (isOpen && !historyLoaded) {
            fetch(`${aiPersonalizationData.restUrl}/chat/history?user_id=${encodeURIComponent(visitorId)}`)
                .then((r) => r.json())
                .then((data) => {
                    if (data.messages && data.messages.length > 0) {
                        setMessages(data.messages);
                    }
                })
                .catch(() => { /* keep default greeting */ })
                .finally(() => setHistoryLoaded(true));
        }
    }, [isOpen]);

    // Salespanel async data fetch (non-blocking).
    useEffect(() => {
        const salespanelId = window.salespanel_id || 'guest';
        console.log('AI Personalization: Async Salespanel check for ID:', salespanelId);
    }, []);

    // Auto-scroll chat to bottom.
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus textarea when chat opens.
    useEffect(() => {
        if (isOpen && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [isOpen]);

    const toggleChat = () => setIsOpen(!isOpen);

    const sendMessage = async () => {
        if (!draft.trim() || isSending) return;

        const userMessage = { role: 'user', content: draft };
        const newMessages = [...messages, userMessage];
        setMessages(newMessages);
        setDraft('');
        setIsSending(true);

        try {
            // STEP 1: Fast chat completion to determine intent.
            const response = await fetch(`${aiPersonalizationData.restUrl}/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    messages: newMessages,
                    post_id: aiPersonalizationData.postId,
                    user_id: visitorId,
                }),
            });

            const res = await response.json();
            const aiMessage = { role: 'assistant', content: res.message };
            setMessages([...newMessages, aiMessage]);

            if (res.layoutUpdated) {
                // STEP 2: Async layout generation.
                // Apply global "agentic" loading state to the whole page
                document.body.classList.add('ai-personalization-loading');

                const generateResponse = await fetch(`${aiPersonalizationData.restUrl}/generate-layout`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        messages: [...newMessages, aiMessage],
                        post_id: aiPersonalizationData.postId,
                        user_id: visitorId,
                    }),
                });

                if (generateResponse.ok) {
                    const resJson = await generateResponse.json();

                    if (resJson.success) {
                        try {
                            const pageResponse = await fetch(window.location.href);
                            const pageHtml = await pageResponse.text();

                            const parser = new DOMParser();
                            const doc = parser.parseFromString(pageHtml, 'text/html');

                            // PHP now tightly wraps the custom blocks in main.wp-site-blocks, so we precisely swap that.
                            const newContent = doc.querySelector('main');
                            const targetContainer = document.querySelector('main');

                            if (newContent && targetContainer) {
                                targetContainer.parentNode.replaceChild(newContent, targetContainer);

                                setTimeout(() => {
                                    document.body.classList.remove('ai-personalization-loading');
                                    newContent.classList.add('ai-personalization-updated');
                                    setIsSending(false);
                                    setTimeout(() => newContent.classList.remove('ai-personalization-updated'), 2000);
                                }, 500);
                            } else {
                                window.location.reload();
                            }
                        } catch (e) {
                            console.error('Failed to parse new DOM in AJAX', e);
                            window.location.reload();
                        }
                    } else {
                        document.body.classList.remove('ai-personalization-loading');
                    }
                    setIsSending(false);
                    return;
                } else {
                    document.body.classList.remove('ai-personalization-loading');
                    setIsSending(false);
                    return;
                }
            }

            setIsSending(false);
        } catch (err) {
            console.error('Chat Error:', err);
            setMessages((prev) => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
            setIsSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const resetLayout = async () => {
        try {
            await fetch(`${aiPersonalizationData.restUrl}/layout?user_id=${encodeURIComponent(visitorId)}&post_id=${aiPersonalizationData.postId}`, {
                method: 'DELETE'
            });
            window.location.reload();
        } catch (e) {
            console.error('Failed to reset layout', e);
        }
    };

    if (!isOpen) {
        return (
            <button className="ai-chatbot-toggle" onClick={toggleChat} aria-label="Open AI Chat">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
            </button>
        );
    }

    return (
        <div className="ai-chatbot-window">
            <div className="ai-chatbot-header">
                <h3>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path></svg>
                    AI Assistant
                </h3>
                <button onClick={toggleChat} aria-label="Close" className="ai-chatbot-close">&times;</button>
            </div>
            <div className="ai-chatbot-messages">
                {messages.map((msg, idx) => (
                    <div key={idx} className={`ai-chatbot-message ai-chatbot-message-${msg.role}`}>
                        <div
                            className="ai-chatbot-message-bubble"
                            dangerouslySetInnerHTML={{ __html: msg.role === 'assistant' ? marked.parse(msg.content) : msg.content }}
                        />
                    </div>
                ))}
                {isSending && (
                    <div className="ai-chatbot-message ai-chatbot-message-assistant">
                        <div className="ai-chatbot-loading">
                            <span className="ai-chatbot-spinner"></span>
                            <span>Thinking...</span>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>
            <div className="ai-chatbot-actions">
                <button className="ai-chatbot-revert" onClick={resetLayout}>
                    Revert Personalization
                </button>
            </div>
            <div className="ai-chatbot-input">
                <textarea
                    ref={textareaRef}
                    className="ai-chatbot-textarea"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your message..."
                    rows="2"
                />
                <button
                    className="ai-chatbot-send"
                    onClick={sendMessage}
                    disabled={isSending || !draft.trim()}
                >
                    {isSending ? '...' : 'Send'}
                </button>
            </div>
        </div>
    );
};

const rootEl = document.getElementById('ai-personalization-chatbot-root');
if (rootEl) {
    createRoot(rootEl).render(<Chatbot />);
}
